import { useState } from "react";
import { Mail, Send, AtSign } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const getSiteDomain = () => {
    if (typeof window === "undefined") return "this site";
    return window.location.host;
};

export const ContactPage = () => {
    useSeo({
        title: "Contact",
        description:
            "Get in touch with the editors. Send feedback, corrections, or partnership inquiries.",
        path: "/contact",
    });

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const domain = getSiteDomain();
    const contactEmail = `hello@${domain}`;

    const handleSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Message from ${name || "Visitor"}`);
        const body = encodeURIComponent(`From: ${name} <${email}>\n\n${message}`);
        window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
        setSubmitted(true);
    };

    return (
        <section className="py-12 sm:py-20" data-testid="contact-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="editorial-label mb-3">Contact</div>
                <h1 className="text-5xl sm:text-7xl font-black tracking-tighter uppercase leading-none mb-6">
                    Get in touch.
                </h1>
                <p className="text-lg text-foreground/70 leading-relaxed mb-12 max-w-2xl">
                    Feedback, corrections, partnership inquiries, or content suggestions —
                    we read every message. There is no support call center here, just the
                    editors. Reach us by email or use the form below.
                </p>

                <div className="bg-card border border-foreground p-6 sm:p-8 mb-12 hard-shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                        <Mail className="h-5 w-5 text-primary" />
                        <span className="editorial-label">Email us directly</span>
                    </div>
                    <a
                        href={`mailto:${contactEmail}`}
                        data-testid="contact-email"
                        className="text-2xl sm:text-3xl font-black tracking-tight hover:text-primary transition-colors break-all inline-flex items-center gap-2"
                    >
                        <AtSign className="h-6 w-6 text-primary shrink-0" />
                        {contactEmail}
                    </a>
                    <p className="text-sm text-foreground/60 mt-3">
                        Typical reply within 2 business days.
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="bg-card border border-foreground p-6 sm:p-10 hard-shadow-sm"
                    data-testid="contact-form"
                >
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                        Send a message.
                    </h2>
                    <p className="text-sm text-foreground/60 mb-8">
                        The form opens your email client with the message pre-filled — no
                        data is sent to a server.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
                        <div>
                            <label htmlFor="name" className="editorial-label block mb-2">
                                Your name
                            </label>
                            <input
                                id="name"
                                data-testid="contact-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="editorial-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="editorial-label block mb-2">
                                Email
                            </label>
                            <input
                                id="email"
                                data-testid="contact-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="editorial-input"
                            />
                        </div>
                    </div>

                    <div className="mb-8">
                        <label htmlFor="message" className="editorial-label block mb-2">
                            Message
                        </label>
                        <textarea
                            id="message"
                            data-testid="contact-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                            rows={6}
                            className="w-full bg-transparent border border-foreground rounded-none p-3 text-base font-body focus:outline-none focus:border-primary resize-y"
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="contact-submit"
                        className="btn-primary-editorial"
                    >
                        Send message <Send className="h-4 w-4 ml-2" />
                    </button>

                    {submitted && (
                        <p
                            className="mt-6 text-sm font-mono uppercase tracking-widest text-primary"
                            data-testid="contact-submitted"
                        >
                            Opening your email client…
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
};
